/** Material for the adventure screen's own furniture.
 *
 * The kit in `theme/` carries the six primitives every screen shares. The rules
 * here are the ones only this screen needs, and they exist because the kit's
 * generic `Frame`/`Button`/`Slot` were being asked to do jobs they were never
 * shaped for: a stat readout that ended up double-boxed (a well inside a well),
 * an end-turn button that read as a flat rectangle, an empty rack slot that
 * rendered as a rule with a stub on the left.
 *
 * It is a separate stylesheet rather than an addition to `THEME_CSS` because
 * `theme/` is shared with every other screen and with three other agents; a
 * rule that only the adventure map wants has no business widening that surface.
 * Both sheets are keyed by element id and mount once.
 *
 * House rules, same as the kit: no radius beyond a 1px mitre, every colour from
 * a token, every metric through `--hm-scale`.
 */


import { reliefDecls } from "../theme/relief.js";
import { StyleLayer, useStyleSheet } from "../theme/styleSheet.js";

export const ADVENTURE_STYLE_ID = "hm-adventure-material";

export const ADVENTURE_CSS = `
/* -------------------------------------------------------------- the plank */
/* The column and the map have to look like one game, and the axis they parted
   on is FIGURE, not brightness.
 *
 * Measured with \`scratchpad/adv-a/stats.py\` over a 1000x700 patch of map and
 * the whole command column, on the shipped 1920x1080 frame and on
 * \a reference screenshot\`, mean
 * luminance / mean saturation / mean 3x3 local contrast.
 *
 * **Neither the script nor the reference is on disk any more**, so the classic HD
 * row below cannot be reproduced and the contrast column cannot be reproduced
 * at all - "mean 3x3 local contrast" is whatever that script computed and
 * nothing states it. Re-measured 2026-08-30 on the three genuine adventure
 * frames that ARE on disk (\a reference screenshot, \`classic20\`, \`classic8\`),
 * map box vs command-column box, the two well-defined statistics are:
 *
 *              map L / S            panel L / S          map:panel S
 *   classic12    60.2 / 0.567         72.0 / 0.579             0.98
 *   classic20    82.8 / 0.526         70.4 / 0.559             0.94
 *   classic8    139.4 / 0.213         72.2 / 0.584             0.36
 *
 * Two things survive the missing file. The panel's own luminance is a CONSTANT
 * of the game - 70.4, 72.0, 72.2 across three completely different maps - and
 * ours at 67.1 is within 7% of it. And the classic's map:panel saturation ratio is
 * 0.98 and 0.94 on the two temperate maps, against the 0.97 and 0.91 the
 * missing files were said to give: the finding below reproduces on files that
 * exist, which is why it is kept. (classic8's 0.36 is a snow map; white ground has
 * no saturation to compare.)
 *
 * The table as it stood:
 *
 *              map                     panel                  panel vs ref
 *   ours    L 94.6  S .661  C 14.67   L 67.1  S .568  C 10.44
 *   The classic HD   L 66.6  S .621  C  9.25   L 65.5  S .602  C 11.75   +2% / -6% / -11%
 *
 * So the panel is within 2.4% of the classic's panel on luminance and is 6% flat on
 * saturation and 11% flat on figure; the 1.41 map-to-panel luminance ratio a
 * reviewer measured is mostly OUR MAP running 42% brighter and 59% busier than
 * The classic's, which is the world renderer's to answer, not this sheet's.
 *
 * What is this sheet's is the 6% and the 11%. Both references put the panel at
 * or ABOVE the map in saturation (the classic HD 0.97x, the expansion 0.91x) while ours sits
 * below it at 1.16x, and a plank with no figure in it is what that reads as.
 * So the column's wood gets its grain back: a quarter-sawn figure blended in
 * \`overlay\`, which raises local contrast and saturation together and leaves
 * mean luminance where the reference already agrees with it.
 *
 * It is a \`::before\` at \`z-index: -1\` rather than a background layer because
 * \`.hm-panel\` gets its whole background from \`reliefDecls\` and re-declaring
 * that here would fork the primitive. \`.hm-panel\` already isolates, so the
 * blend cannot reach past the plank it is painted on, and the negative index
 * keeps the figure under the panel's own contents and over its face. */
.hm-adventure .hm-panel::before {
  content: "";
  position: absolute;
  inset: 0;
  z-index: -1;
  pointer-events: none;
  /* Bands of six uneven widths over a 67px period, not a 26px pinstripe: at a
     short period and even widths the same amplitude reads as corduroy rather
     than as sawn timber, which is a different wrong answer from a flat plank. */
  background-image:
    repeating-linear-gradient(92deg,
      rgba(255, 214, 150, 0.17) 0px, rgba(255, 214, 150, 0.17) 3px,
      rgba(38, 18, 4, 0.21) 3px, rgba(38, 18, 4, 0.21) 11px,
      rgba(255, 214, 150, 0.09) 11px, rgba(255, 214, 150, 0.09) 27px,
      rgba(38, 18, 4, 0.14) 27px, rgba(38, 18, 4, 0.14) 44px,
      rgba(255, 214, 150, 0.14) 44px, rgba(255, 214, 150, 0.14) 52px,
      rgba(38, 18, 4, 0.18) 52px, rgba(38, 18, 4, 0.18) 67px),
    repeating-linear-gradient(177deg,
      rgba(0, 0, 0, 0.13) 0px, rgba(0, 0, 0, 0.13) 1px,
      rgba(255, 224, 170, 0.08) 1px, rgba(255, 224, 170, 0.08) 5px);
  background-size: 134px 134px, 71px 71px;
  mix-blend-mode: overlay;
  opacity: 0.9;
}

/* ---------------------------------------------------------------- numerals */
/* Every number the player reads is set in the SAME serif as the label beside
   it. The kit's \`--hm-font-num\` is a fixed-pitch face with a slashed zero, and
   on this screen that put three typefaces on one panel - a serif "ADELA" over a
   terminal "0 xp", a serif "Wisdom" beside a terminal "1", and a whole resource
   strip of slashed zeros. Nothing in the classic is monospace, and a slashed zero is
   the single strongest "this is a debug overlay" signal a UI can emit.

   Tabular figures are kept, because columns of numbers still have to line up;
   that is a font FEATURE, not a font. Every value also carries the same cut
   shadow the labels do. */
.hm-adventure { --hm-font-num: var(--hm-font-body); }
.hm-adventure .hm-num,
.hm-adventure .hm-adv-stat__value,
.hm-adventure .hm-adv-count,
.hm-adventure .hm-adv-gauge__value {
  font-family: var(--hm-font-body);
  font-variant-numeric: lining-nums tabular-nums;
  font-feature-settings: "tnum" 1, "lnum" 1;
  text-shadow: 0 var(--hm-hair) 0 rgba(0, 0, 0, 0.9);
}

/* Luminance-only grain.
   The kit's noise is \`feTurbulence\` straight into RGBA, so every channel gets
   its own random value: at low opacity over dark wood that is invisible, and on
   a pale gold face it is per-pixel red, green and blue speckle that reads as
   JPEG artifacting rather than as metal. Desaturating the turbulence gives the
   same grain in value only. */
.hm-adventure {
  --hm-noise-lum: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='120' height='120' filter='url(%23n)' opacity='0.55'/%3E%3C/svg%3E");
  /* The same grain at a third the strength, for surfaces that are in shadow.
     A recess is lit by bounce only, so grain at the metal's own contrast reads
     as sand poured into the socket rather than as the plank's own figure. */
  --hm-noise-recess: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.55' numOctaves='3' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='120' height='120' filter='url(%23n)' opacity='0.20'/%3E%3C/svg%3E");
}

/* ------------------------------------------------------- the sidebar plate */
/* The classic's info bar lives at the foot of the command column, not in a strip
   under the map. One of them carries the hover text; there is no second one to
   disagree with it.
 *
 * It was PARCHMENT, and parchment is what made it the loudest object in the
 * lower frame. Measured on the shipped 1920x1080 shot, mean Rec.709 luminance
 * over the bottom strip: resource bar 57, map 61, the date plate 68, END TURN
 * 65 - and this plate 170. A 261x51 near-white slab carrying one five-letter
 * word is 2.6x brighter than everything around it, so the eye goes to the word
 * "Grass" instead of to the map. The classic cuts its terrain readout from the same
 * timber as the rest of the chrome and stamps the name into it in gilt; so
 * does this now, and the whole bottom band is one material again.
 *
 * The height is the RESOURCE BAR's, not the content's. The plate is the last
 * thing in the command column and the resource bar is the last thing under the
 * map, and they are the two halves of one strip: at an intrinsic height the
 * plate's top sat 9px below the bar's and the two read as a step. */
.hm-adv-info {
  display: flex;
  flex-direction: column;
  justify-content: center;
  /* Centred across the plate, the way the classic centres the terrain name in its
     own status bar. The plate is the width of the command column and the
     resting reading is one short word: left-packed, "Snow" over "Slow going"
     sat in the left third of a 253px plate and the other two thirds were bare
     wood with a gilt bead round them, which reads as a plate that failed to
     fill rather than as a caption. */
  align-items: center;
  text-align: center;
  gap: calc(var(--hm-u) * 2);
  padding: calc(var(--hm-u) * 3) calc(var(--hm-u) * 5);
  height: var(--hm-resbar-h);
  min-height: 0;
  overflow: hidden;
  color: var(--hm-gold-4);
${reliefDecls("chamfer", "wood", 2)}
}
/* Both marks are stamped. A serif title over a column of bitmap readings was
   the third type system on this screen; the plate carries the same face the
   hero's own name is cut in, at a magnification \`StatusBar\` measures. */
.hm-adv-info__title {
  display: flex;
  align-items: center;
  color: var(--hm-gold-4);
  /* CLIP, not HIDDEN, everywhere a bitmap reading's box is the ink itself.
     The svg's box is exactly the glyph rectangle; under a fractional device
     ratio it lands on a fractional device pixel while crispEdges snaps each
     texel to a whole one, so an edge row is snapped just past the box and a
     plain clip shaves it. Measured at 1366x768 under an 80% page: the terrain
     plate's \`Grass\` stood 14 rows of the 15-row caption face. A clip margin
     costs no layout and still cuts anything a reader would call a spill. */
  overflow: clip;
  overflow-clip-margin: 2px;
}
.hm-adv-info__line {
  display: flex;
  align-items: center;
  color: var(--hm-text-dim);
  overflow: clip;
  overflow-clip-margin: 2px;
}
.hm-adv-info__line--strong { color: var(--hm-gold-3); }
.hm-adv-info--illegal .hm-adv-info__title { color: #e2a08c; }
.hm-adv-info__badge {
  align-self: flex-start;
  display: flex;
  align-items: center;
  padding: 0 calc(var(--hm-u) * 3);
  color: var(--hm-parch-3);
  background-color: rgba(92, 27, 18, 0.9);
  box-shadow: inset 0 0 0 var(--hm-hair) rgba(0, 0, 0, 0.6);
}

/* -------------------------------------------------------- empty rack slot */
/* A full-width plate, not a socket with a rule trailing off it. An empty slot
   has to read as "a place for a hero"; a 200px hairline reads as a row that
   failed to render. */
.hm-adv-rack {
  display: flex;
  align-items: center;
  gap: calc(var(--hm-u) * 5);
  width: 100%;
  padding: calc(var(--hm-u) * 3);
${reliefDecls("recess", "wood", 2)}
}
.hm-adv-rack__socket {
  flex: 0 0 auto;
  isolation: isolate;
${reliefDecls("recess", "wood", 1)}
}
.hm-adv-rack__label {
  flex: 1 1 auto;
  min-width: 0;
  font-size: var(--hm-fs-tiny);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(185, 166, 131, 0.38);
  text-shadow: 0 var(--hm-hair) 0 rgba(0, 0, 0, 0.8);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ------------------------------------------------------------- stat plate */
/* One box, not two. The kit's \`Frame well\` gave the plate its gilt bead and
   the icon inside it then sat on a second black square of its own, so every
   primary stat was drawn inside two nested boxes. */
.hm-adv-stat {
  display: flex;
  align-items: center;
  justify-content: center;
  /* Was 3u each. The pill is a quarter of the column: at 1366x768 that is 38
     CSS px holding a 24px icon, and 3u of padding either side plus a 3u gap
     left 8px for the reading. One digit fits in 8; a two-digit primary is 11 at
     magnification 1, so every hero past about level ten pushed its own attack
     score over the pill's right lip. Two units of air each side and a two-unit
     gap, with the icon stepping down at 1366 (see \`statIconPx\`), leave 16px
     there against 11 needed, and 23px at 1920 against the 22 a two-digit
     reading takes at magnification 2. A primary cannot pass 99 in the classic, so
     that is the whole range. */
  gap: calc(var(--hm-u) * 2);
  flex: 1 1 0;
  min-width: 0;
  padding: calc(var(--hm-u) * 5) calc(var(--hm-u) * 2);
  /* The medallion screens onto the floor of this socket (\`hm-relief__inlay\`),
     so the pill has to be its own stacking context or the blend reaches the
     panel behind it. */
  isolation: isolate;
  /* A socket cut into the sheet, and it was lit from below: measured on the
     shipped 1080p shot the interior ran L=80.5 at y698 down to L=10.7 at y733
     on all four pills. The primitive is the only recipe now.

     WOOD, not stone: a recess is the panel's own material standing in its own
     shadow, and the panel this one is cut into is a plank. On stone the four
     pills were the only cool objects in the column - measured on the shipped
     1920x1080 frame, a pill's interior means (76,65,45), R/B 1.70, against
     2.37 for a resource plaque, 2.63 for the end-turn key, 2.72 for the bare
     plank and 2.93 for a command key. One material out of five, in the middle
     of the sheet, reading as a slate tile screwed to an oak board. */
${reliefDecls("recess", "wood", 2)}
}
/* The primaries sit BESIDE the portrait, two by two, not in a full-width band
   under it.
 *
 * The row they were in cost the sheet a whole band, and the band beside the
 * portrait was empty because a name and a class line are 40px of type against a
 * 128px face: measured on the shipped 1920x1080 frame that left about 160x57 of
 * bare plank, the largest dead area in the column. The classic sets a hero's numbers
 * against the portrait for the same reason - a face and its numbers are one
 * card - and folding them up there closes the hole and gives each pill half the
 * name column instead of a quarter of the sheet. */
.hm-adv-stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: calc(var(--hm-gutter) / 4);
  /* Pinned to the foot of the portrait row: the pair of rows and the face end
     on one line, which is what makes the two read as one card. */
  margin-top: auto;
  min-width: 0;
}
.hm-adv-stat__value {
  font-family: var(--hm-font-num);
  font-variant-numeric: tabular-nums;
  font-size: var(--hm-fs-body);
  color: var(--hm-gold-4);
  text-shadow: 0 var(--hm-hair) 0 rgba(0, 0, 0, 0.95);
}

/* ------------------------------------------------------- the name strip */
/* The hero's name is cut into a strip, not set on the plank.
 *
 * The classic does this and it is doing work, not decoration: the strip is what tells
 * the eye that the portrait, the name and the four primaries are ONE object -
 * a card - rather than three things that happen to be near each other. Ours had
 * the name floating in the air beside the face with the class under it in a
 * fainter grey, which reads as a caption on a photograph.
 *
 * It spends no height: the name column beside an 85px portrait is about 40px of
 * type in an 85px row, and the strip is cut out of the air that was already
 * there. */
.hm-adv-heroname {
  align-self: stretch;
  min-width: 0;
  display: flex;
  align-items: center;
  padding: calc(var(--hm-u) * 2) calc(var(--hm-u) * 4);
  color: var(--hm-gold-4);
  white-space: nowrap;
  overflow: hidden;
${reliefDecls("recess", "wood", 1)}
}

/* ------------------------------------------------------- portrait socket */
/* A hero's face is SET INTO the column, not laid on it.
 *
 * The kit's \`Frame well\` cuts a stone recess and then the portrait is drawn at
 * the frame's full content size, so the 2px lip the recess paints is covered by
 * the art on all four sides and nothing of the socket survives. Measured on the
 * shipped 1080p shot, the sheet's 85x85 portrait box held 2,824 px of exactly
 * (33,33,33) - the art's own authored ground - and the ring of pixels around it
 * carried no light at all: a photograph pasted onto a plank.
 *
 * Padding the frame is the obvious fix and it is the wrong one: the art snaps
 * to a whole divisor of its 256px source, so 85 (256/3) is the next size down
 * from 128 and taking even 3px off each side drops it to 64 (256/4) - a quarter
 * of the face traded for a margin. The mount is therefore drawn ON the art's
 * own outermost pixels - three beads of it, a dark seat, a gilt bead and an
 * outer shadow line, plus the cast the overhanging lip throws down and in
 * across the face. Zero layout cost, and the ring stops being one flat value:
 * re-shot at 1920x1080, the sixteen pixels running left into the sheet's
 * portrait at mid-height read 74,74,33,33,33,33,33,33,... before - two pixels
 * of panel and then the art's flat ground - and 74,74,23,23,23,146,146,146,
 * 16,16,16,27,27,29,29,30 after, which is a seat, a bead, a shadow line and a
 * cast rising into the picture.
 */
.hm-adv-portrait { position: relative; isolation: isolate; --hm-bead: 1px; }
.hm-adv-portrait::after {
  content: "";
  position: absolute;
  inset: 0;
  pointer-events: none;
  box-shadow:
    inset 0 0 0 var(--hm-bead) rgba(20, 12, 2, 0.92),
    inset 0 0 0 calc(var(--hm-bead) * 2) rgba(246, 228, 171, 0.62),
    inset 0 0 0 calc(var(--hm-bead) * 3) rgba(20, 12, 2, 0.80),
    inset 0 calc(var(--hm-bead) * 2) calc(var(--hm-bead) * 5) rgba(0, 0, 0, 0.55),
    inset calc(var(--hm-bead) * 2) 0 calc(var(--hm-bead) * 5) rgba(0, 0, 0, 0.34),
    inset 0 calc(var(--hm-bead) * -2) calc(var(--hm-bead) * 4) rgba(255, 226, 170, 0.12);
}
/* The mount is three beads wide - seat, gilt, shadow line - so the bead has to
   be set against the picture it surrounds, not once for the screen.
   Re-shot at 1920x1080 with one bead of 2px everywhere, the 32px roster face
   lost 12 of its 32 pixels to its own frame and Jorthas's shoulders with them:
   63% of the socket was still portrait, against 79% on the sheet's 85px face
   at the same setting. One pixel on the small mounts and three on the sheet's
   holds both at about four fifths picture. */
.hm-adv-portrait--large { --hm-bead: 3px; }

/* ---------------------------------------------------------- count plaque */
/* A creature count is a number stamped on a dark plate, never white glyphs
   floating over the art they are counting. */
.hm-adv-count {
  position: absolute;
  /* Inset by the socket's own lip, and seated on the shelf \`InfoPanel\`
     reserved for it (\`COUNT_SHELF_PX\`). It used to be \`right: 0; bottom: 0\`,
     which is not "in the corner of the socket" but "on top of the socket's
     frame": measured on the shipped shots the plaque replaced 14 of the 22
     pixels of the lit bottom lip at 1366x768 and 14 of 33 at 1920x1080, and
     both counts lay across their creature's shins. The frame is the thing that
     says where the socket ends, so nothing may be drawn over it. */
  right: var(--hm-adv-lip, 2px);
  bottom: var(--hm-adv-lip, 2px);
  display: flex;
  align-items: center;
  justify-content: center;
  /* The plaque IS the shelf, exactly. \`line-height\` used to set its height
     from \`--hm-scale\`, so the plate grew fractionally while the bitmap
     numeral inside it stayed on magnification 1 - a 16px plate around a 9px
     reading at 1080p. The mark steps by whole magnifications; so does its
     plate, and here both are on 1. */
  height: var(--hm-adv-shelf, 11px);
  line-height: 1;
  text-align: center;
  padding: 0 calc(var(--hm-u) * 1.5);
  /* A three-digit stack in a 32px socket is wider than the floor it stands on,
     and the plaque then ran out over the socket's own right lip and onto the
     divider between two sockets. The frame says where the socket ends; a
     readout inside it may not cross that line, so the plate is capped at the
     floor's width and its overflow is hidden rather than allowed to spill. */
  max-width: calc(100% - var(--hm-adv-lip, 2px) * 2);
  overflow: hidden;
  font-family: var(--hm-font-body);
  font-variant-numeric: lining-nums tabular-nums;
  /* Smaller than the sheet's body type on purpose. At \`--hm-fs-tiny\` a
     three-digit count was two thirds of a 31px socket and lay across the
     creature it was counting; a plaque is a label on the stack, not a second
     object competing with it. */
  font-size: calc(8px * var(--hm-scale));
  color: var(--hm-gold-4);
${reliefDecls("chamfer", "bronze", 1)}
  /* The chamfer's lips are kept and its FACE is overridden dark, because a
     cream numeral was standing on the bronze face's lit half. Measured on a
     fresh 1366x768 shot at 100%, ink against the ground under the glyphs
     themselves (a box tight to the reading, which is where a reader's eye
     is): 1.86:1 for the 25 and 2.40:1 for a single digit - the plate averages
     4.9:1 over its whole height, so the failure only shows when the sample is
     taken where the type actually sits. Dark face, same cream ink: 9.1:1 at
     1366x768 and 12.1:1 at 1920x1080, both zooms measured. It also puts this
     plaque in the same object language as the battlefield's stack counts,
     which are white on a dark plate with a gold rule, and as the classic's own
     army counts. */
  background-color: #2b1e0c;
  background-image:
    var(--hm-noise-recess),
    linear-gradient(180deg, #4a3517 0%, #2b1e0c 55%, #1a1207 100%);
  background-size: 120px 120px, auto;
  background-repeat: repeat, no-repeat;
  text-shadow: 0 var(--hm-hair) 0 rgba(0, 0, 0, 1);
}

/* -------------------------------------------------------- the end-turn key */
/* The one button that advances the game. Hammered bronze on a deep bevel with
   an engraved glyph, because it must read as pressable across the room. */
.hm-adv-endturn {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: calc(var(--hm-u) * 5);
  padding: calc(var(--hm-u) * 5) calc(var(--hm-u) * 10);
  border: 0;
  cursor: pointer;
  font-family: var(--hm-font-display);
  font-size: var(--hm-fs-body);
  letter-spacing: 0.10em;
  text-transform: uppercase;
  color: #ffeec2;
  /* Cast bronze, not pale sand. The old face was the palest, lowest-contrast
     thing on the screen and shared both its value and its hue with the
     parchment plate beside it, so two unrelated objects read as one material.
     The gradient runs lit-top to dark-bottom across the WHOLE face rather than
     stopping at 14%, which is what makes it a cylinder of metal instead of a
     sticker with a highlight painted on. */
${reliefDecls("chamfer", "bronze", 3)}
  text-shadow:
    0 calc(var(--hm-hair) * -1) 0 rgba(24, 14, 2, 0.95),
    0 var(--hm-hair) 0 rgba(255, 240, 200, 0.28),
    0 calc(var(--hm-u) * 1) calc(var(--hm-u) * 2) rgba(0, 0, 0, 0.75);
}
/* The gilt bead, and the only one of these on the screen.
 *
 * END TURN is the screen's primary action and in the classic it is the most
 * ORNAMENTED control in the frame - ours was the only greyscale object in a
 * gold-and-wood bar and read quieter than the resource chips beside it. The
 * bead is drawn as an overlay rather than folded into the chamfer because
 * \`reliefDecls\` owns the element's whole \`box-shadow\`, and forking the
 * primitive to add one ring to one button is how a kit stops being a kit. */
.hm-adv-endturn::after {
  content: "";
  position: absolute;
  inset: 0;
  pointer-events: none;
  box-shadow:
    inset 0 0 0 var(--hm-hair) rgba(28, 17, 3, 0.9),
    inset 0 0 0 calc(var(--hm-hair) * 3) rgba(246, 214, 132, 0.75),
    inset 0 0 0 calc(var(--hm-hair) * 4) rgba(28, 17, 3, 0.75),
    inset 0 calc(var(--hm-hair) * 5) calc(var(--hm-hair) * 6) rgba(255, 236, 186, 0.16);
}
.hm-adv-endturn:hover:not(:disabled) {
  filter: brightness(1.12);
  color: #fff4d4;
}
/* Pressed is the same cut, inverted: the plate is under the surface now. */
.hm-adv-endturn:active:not(:disabled) {
${reliefDecls("recess", "bronze", 3)}
}
.hm-adv-endturn:focus-visible {
  outline: calc(var(--hm-hair) * 2) solid var(--hm-gold-4);
  outline-offset: calc(var(--hm-hair) * 2);
}
/* Still a carved key when it cannot be pressed - tarnished, not erased. A
   greyed rectangle is how the button read before, and it read as broken. */
/* Still a carved key when it cannot be pressed, and still BRONZE.
   \`saturate(0.42)\` took the hue out of it and landed the face on (74,67,53) -
   R/B 1.40 - in a bar whose every other object is bronze at R/B 2.62, so the
   one control the player is looking for was the deadest, most desaturated
   object in the frame. Tarnish is a loss of light, not a loss of colour. */
.hm-adv-endturn:disabled {
  cursor: default;
  color: #c9ab72;
  filter: brightness(0.80) saturate(0.86);
  text-shadow: 0 calc(var(--hm-hair) * -1) 0 rgba(20, 12, 2, 0.9);
}
.hm-adv-endturn__glyph { flex: 0 0 auto; display: block; }

/* --------------------------------------------------------- the map bezel */
/* The world had no frame at all: 6px of stone along the top and left, 12px
 * along the right and foot, and the art razor-cut mid-sprite at every edge
 * with nothing between it and the panel beside it. The classic sets its map INTO a
 * carved surround, and the surround is what makes the cut read as the frame's
 * own edge instead of as a rendering that ran out.
 *
 * It is an overlay rather than a border because the canvas takes its pixel
 * size from this element's content box, and a border would resize the world
 * every time the bezel's width was touched. Same three beads the portrait
 * mount uses - dark seat, gilt, shadow line - so the map is set into the
 * screen the way a hero's face is set into the column.
 *
 * The negative bottom margin is the fourth side. The grid keeps a collapsed
 * status row between the map and the resource bar, so the map paid TWO gutters
 * at its foot and one everywhere else; pulling one back makes the surround the
 * same width on all four sides. */
.hm-adventure .hm-adventure__map { margin-bottom: calc(var(--hm-gutter) * -1); }
.hm-adventure .hm-adventure__map::after {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 4;
  pointer-events: none;
  box-shadow:
    inset 0 0 0 1px rgba(18, 11, 2, 0.95),
    inset 0 0 0 3px rgba(196, 158, 86, 0.60),
    inset 0 0 0 4px rgba(18, 11, 2, 0.85),
    inset 0 6px 11px rgba(0, 0, 0, 0.55),
    inset 6px 0 11px rgba(0, 0, 0, 0.38),
    inset 0 -5px 9px rgba(0, 0, 0, 0.30),
    inset -5px 0 9px rgba(0, 0, 0, 0.22);
}

/* ------------------------------------------------------- resource plaque */
/* Hammered metal, sized to what is stamped on it, grouped at the left.
   Each plaque used to be a seventh of the bar with the number left-packed
   against the icon, so seven of them held about 770px of blank plate - more
   dead surface than the whole sidebar. A plaque is a readout: it is as wide as
   its reading plus its rim, and the number is centred on it. */
.hm-adv-resrow {
  display: flex;
  align-items: stretch;
  gap: var(--hm-gutter);
  flex: 1 1 auto;
  min-width: 0;
  padding: calc(var(--hm-u) * 3) 0;
}
.hm-adv-res {
  display: flex;
  align-items: center;
  /* CENTRED, not left-packed. The plaque spans its share of the bar so the
     strip has no bare wood in it, and the icon and its reading sit together in
     the middle of the plate with equal margins - which reads as a stamped
     plate. Left-packing put the whole slack on one side of every plaque and
     seven of those was 770px of dead surface reading as unfinished. */
  justify-content: center;
  gap: calc(var(--hm-u) * 4);
  flex: 1 1 0;
  min-width: 0;
  padding: calc(var(--hm-u) * 1) calc(var(--hm-u) * 4);
${reliefDecls("chamfer", "wood", 2)}
}
/* The resource icons are authored as full-bleed 96px tiles with their own dark
   ground, so on a plaque each one read as a loose black square sitting next to
   a number. A hairline gilt bead and a seat shadow turn that square into a
   medallion SET INTO the plate, which is what it is meant to be. */
/* The medallion is set INTO the plate, and its art is authored on its own
   opaque black ground - so the socket is a relief and the sprite screens onto
   the socket's floor. Without the blend the floor is #000 and the medallion is
   a hole. */
.hm-adv-res__socket {
  flex: 0 0 auto;
  display: flex;
  padding: calc(var(--hm-u) * 1);
${reliefDecls("recess", "bronze", 1)}
}
/* --------------------------------------------------------- the date plate */
/* The classic does not print the day on bare wood; it is stamped on a plate beside
   the turn key. Ours was two lines of loose serif floating on the plank between
   the treasury's seven chamfered plaques and a cast-bronze button - the only
   thing in the bar with no edge on it, and therefore the only thing that read
   as text laid over the furniture instead of cut into it. */
.hm-adv-date {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  gap: calc(var(--hm-u) * 2);
  flex: 0 0 auto;
  padding: calc(var(--hm-u) * 3) calc(var(--hm-u) * 5);
${reliefDecls("chamfer", "wood", 2)}
}
.hm-adv-date__line {
  display: flex;
  align-items: center;
  color: var(--hm-gold-4);
}
.hm-adv-date__line--dim { color: var(--hm-text-dim); }
/* ------------------------------------------------------------- the aim plate */
/* Struck on the same chamfered plate as the calendar, because it is the same
   kind of reading: a standing fact about the scenario rather than a number that
   moves. It is the ONLY flexible item in the bar - everything else is fixed at
   its own width - so the slack a wide window leaves collects here instead of
   spreading the treasury plaques apart, and a narrow one squeezes it to nothing
   before anything else is touched. The zero min-width is what makes that true:
   a flex item defaults to an automatic minimum and would refuse to shrink below
   its own text, which is exactly how a bar overflows its window. */
.hm-adv-aim {
  display: flex;
  align-items: baseline;
  gap: calc(var(--hm-u) * 3);
  flex: 1 1 0;
  min-width: 0;
  padding: calc(var(--hm-u) * 3) calc(var(--hm-u) * 5);
${reliefDecls("chamfer", "wood", 2)}
}
.hm-adv-aim__label {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  color: var(--hm-text-dim);
}
/* One line, clipped with an ellipsis rather than wrapped: the bar's height is
   fixed by the treasury plaques and a second line would push them. */
.hm-adv-aim__text {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: var(--hm-fs-body);
  color: var(--hm-gold-4);
}
.hm-adv-res__value {
  font-size: var(--hm-fs-body);
  color: var(--hm-gold-4);
  /* A gold purse is six glyphs and a gem count is two; a fixed minimum keeps
     the seven readings from stepping like a bar chart.

     CENTRED in that minimum, not packed against its left edge. The mark is an
     SVG and \`text-align\` never reached it, so every reading narrower than the
     minimum sat hard against the icon and the plaque's ink centroid moved with
     the number: measured on the shipped 1920x1080 frame, six of the seven
     plaques carried their ink 8px left of plate centre and the gold one, whose
     \`20,000\` overruns the minimum, 5px right - a strip that visibly steps
     along its own length. Centring the mark inside the minimum makes the
     icon-and-reading pair symmetric, so all seven land on one axis whatever
     they read. */
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: calc(var(--hm-u) * 26);
  white-space: nowrap;
}

/* ------------------------------------------------------ the column's share */
/* The rack no longer takes the slack: with four slots it would leave a fifth of
   the column as blank wood under them. The slack goes to the hero sheet, which
   was the panel being squeezed. */
.hm-adv-rackpanel { flex: 0 0 auto; }
/* The towns band does not give: it is one row deep with one town, and a
   shrink of six pixels is what cut the roster row through the middle of its
   thumbnail at 1366x768 (the scroll area then folds at the row above, which
   with one row means the row itself). It grows with its content up to a
   quarter of the column and scrolls past that; the slack it no longer gives
   comes out of the sheet, which folds a whole row and says so. */
.hm-adv-townpanel { flex: 0 0 auto; min-height: 0; max-height: 25%; }
.hm-adv-sheet { flex: 1 1 auto; min-height: 0; overflow: hidden; }
/* The secondary-skill rail goes when the window is short, and it goes WHOLE.
 *
 * The sheet is the panel the column's deficit lands on, and at 1366x768 with
 * the browser at 100% its content is 203 CSS px in a 174px box - the rail's
 * two rows exactly. What used to happen then was that the scroll area cut the
 * last row through the middle; what happens now is that it folds to the last
 * whole row, which paints the rail's rows nowhere while the DOM still reports
 * boxes for them. Both are worse than not asking for the rail: the classic's
 * own adventure sidebar carries a portrait, four primaries, the gauges and the
 * army, and NO secondary-skill list - the hero sheet is where skills are read.
 * So on a window this short the rail is not rendered at all, and the sheet
 * fits without a fold.
 *
 * By CSS height, not device height: the question is how many CSS pixels the
 * column has to lay out in, which is what a zoom actually changes (a 768-row
 * window is 768 CSS px at 100% and 960 at 80%, and at 80% the rail fits). */
@media (max-height: 820px) {
  .hm-adv-skillrail { display: none; }
}
/* The rack is the panel's floor and it does not give.
 *
 * It is a bare \`.hm-row\` pinned under the scroll area, so it inherited flex's
 * default \`0 1 auto\` and SHRANK whenever the sheet's content wanted the room -
 * but its seven sockets carry a pixel height of their own, so what actually
 * happened is that the row's box shrank under its contents and the sheet's
 * \`overflow: hidden\` sheared the bottom off: the sockets lost their lit lower
 * lip and the count plaques lost their bottom border row. The scroll area is
 * the thing on this panel that is allowed to give, and it already scrolls. */
.hm-adv-armyrow { flex: 0 0 auto; }

/* ----------------------------------------------------------- skill line */
/* A named skill and its mastery, not a table of contents. The dotted leader is
   a print convention for a page of prices; between two words six pixels apart
   it is just noise, and it made the sheet read as HTML rather than as a sheet. */
.hm-adv-skill {
  display: flex;
  align-items: center;
  gap: calc(var(--hm-u) * 4);
  /* One unit of air above and below, not four: the row's marks are the
     window's cap floor tall (15 device rows on a 1080p window since the
     floor scaled with the window - see \`deviceCapFloor\`) and two rows at
     four units pushed the sheet 31px past its column at 1920x1080, which hid
     the second skill behind the rack. The rank chip carries its own air. */
  padding: 0 calc(var(--hm-u) * 2);
  box-shadow: inset 0 calc(var(--hm-hair) * -1) 0 rgba(0, 0, 0, 0.45),
    inset 0 calc(var(--hm-hair) * -2) 0 rgba(255, 226, 170, 0.06);
}
/* The skill mark is a medallion in a socket, the same object the resource bar
   sets its ore and gems in. Without the socket the icon is a 16px black square
   lying on the plank - measured on the shipped 1366x768 shot, Wisdom and Air
   Magic each carried a 16x16 block of exactly (0,0,0). */
.hm-adv-skill__socket {
  flex: 0 0 auto;
  display: flex;
  isolation: isolate;
  padding: calc(var(--hm-u) * 1);
${reliefDecls("recess", "bronze", 1)}
}
/* Both marks are stamped, for the reason the gauges' captions are: a serif
   "Leadership" and a serif "BASIC" sat directly under three bitmap gauges and
   four bitmap stat figures, twenty pixels from a bitmap "Elifell". The
   sheet is one card and a card is set in one face. */
.hm-adv-skill__name {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  color: var(--hm-text);
  /* clip, not hidden - see \`.hm-adv-info__title\`. */
  overflow: clip;
  overflow-clip-margin: 2px;
}
.hm-adv-skill__rank {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  padding: calc(var(--hm-u) * 1) calc(var(--hm-u) * 3);
  /* Not --hm-gold-4 (#f4e0a3): the mastery word is the ONE reading in the
     sidebar that did not clear the text lane's 4.5:1 floor. Measured on a
     fresh 1920x1080 shot at 100% (modal ink against the modal colour of the
     chamfer's own bronze face, 128,102,51): 4.15:1, and 4.45:1 at 1366x768 at
     80%. The chip is small, its ground is a lit gradient rather than a flat
     colour, and it sits on wood whose grain moves under it, so it has to hold
     a margin rather than sit on the line. This cream reads 5.11:1 on the same
     ground at 1080p and 5.48:1 on the laptop's. */
  color: #fff6dd;
${reliefDecls("chamfer", "bronze", 1)}
}

/* --------------------------------------------------------------- gauges */
/* A gauge is a groove cut into the panel with its caption and its reading cut
   into the two ends, not a coloured div.

   The kit's \`Bar\` was two flat single-hue rectangles with a 1px light line on
   top, unlabelled, both near full, and the mana one was a cornflower blue that
   appears nowhere else on a screen of wood and gilt. Nothing told the player
   what either measured. This carries its own words. */
.hm-adv-gauge {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: calc(var(--hm-u) * 3);
  /* 17 units, never under 18px: a caption at the laptop floor (11 rows and
     a 3-row descender) plus the two lips needs 18, and at 1920x1080 the
     window's floor is 15 rows and 17 units is 26. */
  height: max(18px, calc(var(--hm-u) * 16));
  padding: 0 calc(var(--hm-u) * 4);
  overflow: hidden;
${reliefDecls("recess", "wood", 2)}
}
/* The groove the fill runs in, inset by the socket's own lip.
 *
 * \`reliefDecls("recess", …, 2)\` draws a 2px edge - flat px, not scaled - and
 * the fill used to be \`left:0; top:0; bottom:0\`, so a bar at 100% painted over
 * all four lips and the reading became a flat coloured rectangle with no cut
 * around it. Movement and mana are BOTH at 100% on the turn a game starts, so
 * that was the state the screen shipped in. Clipping the fill to the groove
 * keeps the lit lip visible at every value, full included. */
.hm-adv-gauge__groove {
  position: absolute;
  inset: 2px;
  overflow: hidden;
  pointer-events: none;
}
.hm-adv-gauge__fill {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  /* The fill is liquid in a GROOVE, so it is lit like the floor of one: darkest
     under the overhanging top lip, lightening toward the foot where bounce
     reaches it. \`relief.ts\` names the inverse - bright top, dark foot - as the
     one relation these screens keep getting backwards, and this fill had it:
     measured on the re-shot 1920x1080 frame, the movement bar ran L=189 on its
     top row down to L=110 at its foot and the mana bar L=161 down to L=82.
     Both were chamfers painted inside a recess.
     
     It is also why the captions washed. The mark is cream (L=220) with a black
     keyline, and over a fill topping out at 189 the letterforms had 31 points of
     separation from their own ground - the keyline was doing all the work.
     Turning the ramp the right way up AND holding its light end below the ink
     gives the type a dark ground down the whole trough. */
  /* The overlay was 0.50/0.36/0.20 and the type still washed at the foot of
     the ramp. Measured on fresh shots, ink against the ground under the glyphs
     themselves: the movement reading stood at 3.60:1 at 1366x768 at 80% and
     4.19:1 at 1920x1080 at 100% - the two lowest figures on the adventure
     screen and both under the lane's 4.5:1 floor. Ten more points of black at
     the foot puts the light end of the fill under the ink everywhere, which is
     what the note above says this ramp is for. */
  background-image:
    linear-gradient(180deg,
      rgba(0, 0, 0, 0.58) 0%,
      rgba(0, 0, 0, 0.47) 55%,
      rgba(0, 0, 0, 0.33) 100%),
    linear-gradient(90deg, var(--hm-gauge-a) 0%, var(--hm-gauge-b) 100%);
  box-shadow:
    /* The lit lip at the foot of the groove, and the shadow the column of
       liquid casts against the dry floor ahead of it. */
    inset 0 calc(var(--hm-hair) * -1) 0 rgba(255, 226, 170, 0.16),
    calc(var(--hm-hair) * 1) 0 0 rgba(0, 0, 0, 0.65);
}
/* Movement is the wood-and-gilt gold the rest of the column is cut from; mana
   is a lapis that sits in the same family rather than a UI blue. */
.hm-adv-gauge--move { --hm-gauge-a: #8a6a1c; --hm-gauge-b: #d9b25a; }
.hm-adv-gauge--mana { --hm-gauge-a: #2b3f74; --hm-gauge-b: #6f8ec9; }
.hm-adv-gauge--xp   { --hm-gauge-a: #4a3a12; --hm-gauge-b: #9a7c33; }
/* Caption and reading are BOTH bitmap marks now.
 *
 * They shared one plate and did not share a face: the caption was the kit's
 * anti-aliased serif at \`--hm-fs-tiny\` and the reading beside it was a
 * hard-edged two-colour bitmap numeral, six pixels apart on a 162px groove.
 * Two type systems on one object is the loudest "this is a web page" tell the
 * column had left, and the fix is not to vectorise the numeral - the numeral is
 * right - it is to cut the word from the same face. \`glyphs.ts\` carries the
 * full set of capitals for exactly this: short plaque labels, not prose.
 *
 * There is nothing to style here but position and colour: a \`BitmapText\` is an
 * SVG that takes its ink from \`currentColor\`, so \`font-size\` and
 * \`text-shadow\` would do nothing at all. The keyline the glyph draws around
 * itself replaces the cut shadow the serif needed. */
.hm-adv-gauge__caption,
.hm-adv-gauge__value {
  position: relative;
  display: inline-flex;
  align-items: center;
  line-height: 1;
  color: #fff8e6;
}
.hm-adv-gauge__caption {
  min-width: 0;
  /* clip, not hidden - see \`.hm-adv-info__title\`. */
  overflow: clip;
  overflow-clip-margin: 2px;
  opacity: 0.94;
}
.hm-adv-gauge__value { flex: 0 0 auto; }

/* ------------------------------------------------------------- the minimap */
/* The map's own extent IS the well. There is no second border inside the frame
   and no slate margin outside the map: the panel used to be three concentric
   empty rectangles because the image was quantised to a whole pixel per cell
   and then centred in a square well it could not fill. */
.hm-adv-minimap { overflow: hidden; }
.hm-adventure .hm-minimap { image-rendering: pixelated; }

/* -------------------------------------------------------- the band caption */
/* The heading over the hero rack and the town rack.
 *
 * Both were a full \`hm-title\` - the 15px display face the hero's own name is
 * set in - inside a panel carrying its full padding, so "TOWNS (1)" spent about
 * 45 device pixels of a 690px column introducing a single row. A heading in a
 * command column is a caption on a drawer, not a title page: small caps, tight,
 * with the count set apart from the word instead of bracketed after it, and an
 * engraved rule under it doing the separating that the whitespace used to do. */
/* Four units and not five: the column is over-full at both viewports this
   build targets, and the six device pixels the two bands give back here are
   what let the hero sheet carry its secondary-skill rows at 1920x1080 (253
   device rows of content in a 248-row box before, 254 after). A band's air is
   the cheapest six pixels on the column. */
.hm-adv-band { padding: calc(var(--hm-u) * 4); }
.hm-adv-band__title {
  flex: 0 0 auto;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: calc(var(--hm-u) * 3);
  padding-bottom: calc(var(--hm-u) * 3);
  margin-bottom: calc(var(--hm-u) * 3);
  line-height: 1;
  color: var(--hm-gold-3);
  white-space: nowrap;
  /* clip, not hidden - see \`.hm-adv-info__title\`. */
  overflow: clip;
  overflow-clip-margin: 2px;
  /* A cut line: dark groove, lit lip below it - and the title stands on a
     DARKENED strip of the plank rather than on the plank itself. Measured on
     the 1366x768 shot at 100%: gold ink on bare wood grain read 4.41:1 for
     the word Heroes and 4.49:1 for Towns, either side of this lane's floor,
     and the grain's own luminance varies enough under a caption to move that
     by a tenth in either direction. The classic sets its band titles on a
     near-black inset strip for the same reason; a fifth of black under the
     word takes both readings to 6:1 and costs no layout. */
  padding-left: calc(var(--hm-u) * 2);
  padding-right: calc(var(--hm-u) * 2);
  margin-left: calc(var(--hm-u) * -2);
  margin-right: calc(var(--hm-u) * -2);
  background-color: rgba(10, 7, 4, 0.34);
  box-shadow:
    inset 0 calc(var(--hm-hair) * -1) 0 rgba(0, 0, 0, 0.65),
    0 var(--hm-hair) 0 rgba(255, 226, 170, 0.12);
}
.hm-adv-band__count {
  flex: 0 0 auto;
  /* The count is the last thing before the panel's gilt bead, and at
     \`--hm-scale\` 1 the caption's own padding is all that stood between them:
     measured on the 1366x768 shot the "1" over HEROES ended at x1355 with the
     bead at x1360, five pixels, against nine at 1080p. A reading that close to
     a bright edge reads as clipped even when it is not. The pad is in \`--hm-u\`
     so it steps with the rest of the column. */
  padding-right: calc(var(--hm-u) * 2);
  display: flex;
  align-items: center;
  color: var(--hm-text-faint);
}

/* ------------------------------------------------------------ roster state */
/* What replaced the roster row's two gauges: one word about whether this hero
   still has a turn in them. See the note at the head of HeroList. */
.hm-adv-roster__state {
  display: flex;
  align-items: center;
  /* One step over --hm-text-faint: #c9b894 measured 4.67-4.99:1 on the
     roster wood (94,66,36) at 80%; #e2d3ad is 6.2 there. Raised again after a
     fresh sweep found the SELECTED row's plate, which is lighter than the
     wood: the L1 badge stood at 4.39:1 against (124,89,48) at 1920x1080 and
     4.44 at 80%, i.e. under the floor on the one row a player is looking at.
     #f7ecd2 is 5.37:1 there and 7.84 on the plain wood. */
  color: #f7ecd2;
}
.hm-adv-roster__state--spent { color: #9c6a52; }

/* ------------------------------------------------------------ army socket */
/* A recess CUT INTO the panel, never a black rectangle.
 *
 * Five of the seven sockets are empty on a day-one army and they are ~70% of
 * the rack's width, so whatever they read as is what the rack reads as. Flat
 * near-black read as holes punched through the wood - one reviewer called the
 * row missing art, another called it a debug overlay - because a hole has no
 * light on it anywhere and every other object in the column does.
 *
 * A recess has four things a hole does not: the panel's own shadow falling in
 * over the top and left lips, the far wall catching the light at the bottom and
 * right, a floor warm enough to still be wood, and the same grain as the plank
 * it is cut from. All four are here, and the filled and empty sockets share
 * every one of them so a two-stack army reads as one rack with two stacks in it
 * rather than as two tiles beside five holes. */
.hm-adventure .hm-slot--empty,
.hm-adventure .hm-adv-army {
  opacity: 1;
  isolation: isolate;
${reliefDecls("recess", "wood", 3)}
}

/* --------------------------------------------------------------- army slot */
/* Taller than it is wide, because a creature is. The art inside is fitted by
   the sprite's OWN bounding box to the socket less a common inset (see
   \`fitCreature\` in InfoPanel), so a spearwarden whose frame carries a two-foot pike
   above his head and an archer whose frame is all archer end up the same
   distance from every wall, standing on one shared floor line. Fitting to the
   box instead put a small figure in one socket and a wall-to-wall one in the
   next. */
.hm-adventure .hm-adv-army {
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}
/* The creature stands ON the socket's floor, on the same line as its
   neighbours - a rack of figures floating at different heights reads as a
   sprite sheet, not as an army. */
.hm-adv-army__art { flex: 0 0 auto; }

/* --------------------------------------------------------- command keys */
/* Five keys in a row under the racks, struck from the SAME bronze as END TURN.
   They were cut stone, and back when \`--hm-stone-2\` was #272d36 that was the
   only cool grey on a column of walnut and gilt (the ramp is warm now: #322d24).
   With the end-turn key 460px away wearing a lit top lip, a chamfer and a cast
   shadow, the mismatch was visible in one glance.
   A command key and the turn key are the same class of object - a plate you
   press - so they are struck from one material and only the size of the plate
   and the mark on its face differ. */
/* Seven keys, four to a row, and the short row is CENTRED.
 *
 * It was \`repeat(5, 1fr)\`, which puts five keys on the first row and two on the
 * second hard against the left rail with three empty cells beside them. Three
 * holes in the bottom-right corner of a carved block is the shape of a grid
 * that ran out of content, and a reviewer reads it as unfinished art rather
 * than as seven keys. Four and three, both rows centred, is a deliberate
 * arrangement: the block has one axis of symmetry and no holes. */
.hm-adv-keys {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--hm-gutter);
  flex: 0 0 auto;
}
.hm-adv-key {
  position: relative;
  /* Four to a row, less the three gutters between them. The basis is a width
     and not a count, so a host that adds an eighth key gets two full rows
     rather than a reflow nobody asked for. */
  flex: 0 1 calc((100% - var(--hm-gutter) * 3) / 4);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: calc(var(--hm-u) * 1);
  padding: calc(var(--hm-u) * 4) calc(var(--hm-u) * 1);
  border: 0;
  cursor: pointer;
  /* The level switch sets its mark in a screened medallion; the blend must not
     reach past the key's own face. */
  isolation: isolate;
  /* Cast bronze: lit at the top of the face, falling away to a dark foot, so
     the plate reads as a cylinder of metal rather than a rectangle with a
     highlight painted on. Identical stops to \`.hm-adv-endturn\`. */
${reliefDecls("chamfer", "bronze", 2)}
}
.hm-adv-key:hover:not(:disabled) { filter: brightness(1.16); }
/* Pressed: the plate sinks into its seat, so the lit top lip becomes the
   shadow the wood casts down onto it - the same primitive, inverted. */
.hm-adv-key:active:not(:disabled) {
${reliefDecls("recess", "bronze", 2)}
}
.hm-adv-key:focus-visible {
  outline: calc(var(--hm-hair) * 2) solid var(--hm-gold-4);
  outline-offset: calc(var(--hm-hair) * -2);
}
/* Tarnished, not erased - the same rule the end-turn key follows. A key that
   greys out to a flat rectangle reads as broken art. */
.hm-adv-key:disabled {
  cursor: default;
  filter: saturate(0.42) brightness(0.74);
}
/* ------------------------------------------------------- the level switch */
/* Surface / underground, directly under the minimap. The row and its pressed
   state used to be inline styles on the component, which is how two of them -
   \`hm-adv-levels\` and \`hm-adv-level\` - ended up as class names with no rule
   behind them at all: a name in the markup that the stylesheet has never heard
   of is indistinguishable from a rule that was deleted by accident. */
.hm-adv-levels {
  display: grid;
  gap: var(--hm-gutter);
  margin-top: var(--hm-pad);
}
/* Not the current level: the plate is still bronze, just unlit. */
.hm-adv-level { filter: saturate(0.72) brightness(0.86); }
/* The current one is held down. \`.hm-adv-key\`'s pressed look is a \`:active\`
   rule and this button is not in \`:active\`, so the seated state is spelled out
   against the attribute that actually carries it. */
.hm-adv-level[aria-pressed="true"] {
  filter: brightness(1.1);
  outline: calc(var(--hm-hair) * 2) solid var(--hm-gold-4);
  outline-offset: calc(var(--hm-hair) * -2);
}
.hm-adv-level[aria-pressed="true"] .hm-adv-key__label { color: var(--hm-gold-4); }

/* Full text, not dim: the label sits on a LIT bronze plate, measured (111, 85,
   35) under \`Menu\` at 1920x1080, and dim text was 2.9:1 there (\`gold-3\` on
   the pressed level was 4.3:1). \`text\` is 5.4:1 and \`gold-4\` 5.4:1. */
.hm-adv-key__label {
  display: flex;
  align-items: center;
  color: var(--hm-text);
  /* clip, not hidden - see \`.hm-adv-info__title\`. Measured at 1920x1080
     under an 80% page: \`Surface\` stood 14 rows of its 15-row face. */
  overflow: clip;
  overflow-clip-margin: 2px;
  max-width: 100%;
}

/* --------------------------------------------------------- the system key */
/* Save, load, settings and the way out.
 *
 * It was a floating \`.hs-btn\` at left:10/top:10 with a drop shadow, sitting on
 * the map art - the full-screen-menu kit's material and type on a screen built
 * from \`.hm-adv-*\` bronze, which made it a fourth material and a third type
 * register on terrain the player wants to click. It is a key in the resource
 * bar now, struck from the same bronze as END TURN and the command block, and
 * the map is clear from edge to edge. */
.hm-adv-syskey {
  flex: 0 0 auto;
  align-self: center;
  flex-direction: row;
  gap: calc(var(--hm-u) * 3);
  padding: calc(var(--hm-u) * 4) calc(var(--hm-u) * 5);
}

/* ------------------------------------------ landscape-phone overlay HUD */
/* The map is full-bleed. Chrome is absolutely placed over it and the gaps
   between plates let pointer events through to the canvas (D8). */
.hm-adventure--phone {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: minmax(0, 1fr);
  grid-template-areas: "map";
  position: relative;
  width: 100%;
  height: 100%;
  padding: 0;
  gap: 0;
  overflow: hidden;
  --hm-sidebar: 0px;
  --hm-status-h: 0px;
  --hm-resbar-h: 0px;
}
.hm-adventure--phone .hm-adventure__map {
  grid-area: map;
  position: relative;
  inset: auto;
  width: 100%;
  height: 100%;
  margin: 0;
}
.hm-phone {
  grid-area: map;
  position: absolute;
  inset: 0;
  z-index: 4;
  pointer-events: none;
  padding: var(--hm-safe-top) var(--hm-safe-right) var(--hm-safe-bottom) var(--hm-safe-left);
}
.hm-phone__top,
.hm-phone__minimap,
.hm-phone__rail,
.hm-phone__keys-toggle { pointer-events: auto; }
.hm-phone--keys .hm-phone__bottom { pointer-events: auto; }
.hm-phone__top {
  position: absolute;
  top: var(--hm-safe-top);
  left: var(--hm-safe-left);
  right: var(--hm-safe-right);
  z-index: 5;
  height: 24px;
  min-height: 24px;
}
.hm-phone__top .hm-panel {
  min-height: 24px;
  height: 24px;
  padding: 0 2px !important;
}
.hm-phone__top .hm-adv-date,
.hm-phone__top .hm-adv-endturn > :not(.hm-adv-endturn__glyph),
.hm-phone__top .hm-adv-syskey .hm-adv-key__label { display: none; }
.hm-phone__top .hm-adv-resrow { overflow-x: auto; -webkit-overflow-scrolling: touch; padding: 0; gap: 2px; }
.hm-phone__top .hm-adv-res { gap: 2px; }
.hm-phone__top .hm-adv-res__socket { width: 14px; height: 14px; }
.hm-phone__top .hm-icon { width: 14px !important; height: 14px !important; }
.hm-phone__top .hm-adv-endturn {
  min-height: 22px; min-width: 22px; height: 22px; width: 22px;
  padding: 0; gap: 0;
}
.hm-phone__top .hm-adv-endturn__glyph { width: 12px; height: 14px; }
.hm-phone__top .hm-adv-syskey {
  min-height: 22px; min-width: 22px; height: 22px; width: 22px;
  padding: 0; gap: 0;
}
.hm-phone__top .hm-adv-syskey svg { width: 12px; height: 14px; }
.hm-phone__minimap {
  position: absolute;
  top: calc(var(--hm-safe-top) + 28px);
  left: var(--hm-safe-left);
  width: 88px;
  z-index: 5;
}
.hm-phone__minimap .hm-panel { padding: 3px; }
.hm-phone__zoom {
  display: flex;
  gap: 4px;
  margin-top: 4px;
  pointer-events: auto;
}
.hm-phone__zoom-btn {
  flex: 1 1 auto;
  min-width: 28px;
  min-height: 28px;
  padding: 0;
  border: 0;
  cursor: pointer;
  color: var(--hm-text);
  font-size: max(18px, var(--hm-type-floor));
  line-height: 1;
${reliefDecls("chamfer", "bronze", 2)}
}
.hm-phone__rail {
  position: absolute;
  top: calc(var(--hm-safe-top) + 28px);
  right: var(--hm-safe-right);
  bottom: calc(var(--hm-safe-bottom) + 28px);
  width: 36px;
  z-index: 5;
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
}
/* Clear of the whole bottom cluster, measured against its TALLEST state, not
   its usual one: 4px padding + a 32px key row + a 3px gap + a reading plate
   that grows to 64px when it has three lines to say. It was 72px, which fitted
   the 22px plate the panel used to squeeze the line down to and overlapped the
   40px one by seven pixels the moment it stopped squeezing. An overlap here is
   an invisible scroll container lying over the top edge of the plate, which is
   the kind of dead tap nobody can explain. */
.hm-phone--keys .hm-phone__rail { bottom: calc(var(--hm-safe-bottom) + 104px); }
.hm-phone__rail .hm-adv-band__title,
.hm-phone__rail .hm-entity-row .hm-col,
.hm-phone__rail .hm-adv-rack__label,
.hm-phone__rail .hm-label { display: none; }
.hm-phone__rail .hm-panel { padding: 2px; }
.hm-phone__rail .hm-entity-row,
.hm-phone__rail .hm-adv-rack {
  width: 32px;
  height: 32px;
  min-height: 32px;
  padding: 2px;
  gap: 0;
}
.hm-phone__bottom {
  position: absolute;
  /* Full width: the reading line spans the edge while it is drawn, and a line
     only as wide as its own text would be a caption floating in the
     bottom-right corner of the map. */
  left: var(--hm-safe-left);
  right: var(--hm-safe-right);
  bottom: var(--hm-safe-bottom);
  z-index: 5;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 3px;
  padding: 0 4px 4px;
  pointer-events: none;
  background: none;
}
.hm-phone__bottom > * { pointer-events: auto; }
.hm-phone__bottom { align-items: stretch; }
/* The bottom line: keys in one corner, the toggle in the other. space-between
   and not a gap, so the two clusters sit ON the corners at every phone width
   rather than near them. */
.hm-phone__keysrow {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 6px;
  pointer-events: none;
}
.hm-phone__keysrow > * { pointer-events: auto; }
/* Hidden by the attribute rather than unmounted, so the panel keeps its
   identity for aria-controls and a screen reader is told what the toggle
   operates before it is opened. */
.hm-phone__keyscluster[hidden] { display: none; }
.hm-phone__keyscluster {
  min-width: 0;
  flex: 1 1 auto;
}
/* Two lines of reading, three when there are three to say: the plate stamps a
   title and its detail - "Crystal Cavern" over "Guarded", "Snow" over "Slow
   going" - and the 22px plate this used to shrink to while the keys were open
   could only ever show the first of them. It no longer shrinks, because it is
   no longer drawn in the other state at all: the plate and the key panel are
   shown and hidden together, so the only size it has to fit is this one. */
.hm-phone__bottom .hm-adv-info {
  /* Two lines of room, three when there are three to say - an object with an
     owner and a note is a stack that deep, and a fixed 40px plate cut the last
     of them off by 8px. Beyond three the plate holds and the overflow is
     clipped, which is the rule the desktop plate keeps as well. */
  height: auto;
  min-height: 40px;
  max-height: 64px;
  overflow: hidden;
  padding: 2px 6px;
}
.hm-phone__bottom .hm-adv-keys {
  flex-wrap: nowrap;
  justify-content: flex-start;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}
.hm-phone__bottom .hm-adv-key {
  flex: 0 0 32px;
  width: 32px;
  height: 32px;
  min-width: 32px;
  padding: 0;
}
.hm-phone__keys-toggle {
  align-self: flex-end;
  /* Pinned to the right corner whether or not the key cluster is beside it.
     With space-between alone the toggle walked to the LEFT corner every time
     the panel closed, so the one control that is always on screen moved every
     time it was used. */
  margin-left: auto;
  min-width: 44px;
  min-height: 24px;
  padding: 0 8px;
  border: 0;
  cursor: pointer;
  color: var(--hm-text);
${reliefDecls("chamfer", "bronze", 2)}
}
.hm-phone__keys-toggle[aria-expanded="true"] {
${reliefDecls("recess", "bronze", 2)}
}
/* The Info key: the same plate as Keys, to its right, pressed while the
   card is up. It never takes the auto margin - Keys owns the corner and the
   Info key rides beside it, so the pair moves together or not at all. */
.hm-phone__info-key {
  align-self: flex-end;
  min-width: 44px;
  min-height: 24px;
  padding: 0 8px;
  border: 0;
  cursor: pointer;
  color: var(--hm-text);
${reliefDecls("chamfer", "bronze", 2)}
}
.hm-phone__info-key[aria-pressed="true"] {
${reliefDecls("recess", "bronze", 2)}
}
`;

/**
 * Mount `ADVENTURE_CSS` once, refcounted, exactly as `GameTheme` mounts the
 * kit's sheet. Refcounting rather than "insert and forget" so a test that
 * mounts and unmounts the screen does not leave the document dirty.
 */
export function useAdventureMaterial(): void {
  useStyleSheet(ADVENTURE_STYLE_ID, ADVENTURE_CSS, StyleLayer.adventure);
}

